/**
 * Company settings shapes (`GET/PUT /api/settings`, BUILD_PLAN cross-cutting
 * conventions) plus the client-side validation the Save button runs before
 * the worker's Zod does. `sms` is read-only status derived from the worker's
 * env; the PUT body carries only the editable keys.
 */

export interface ContactHours {
  /** IANA zone, e.g. `America/Chicago`. */
  tz: string;
  /** `HH:MM` 24 h. */
  start: string;
  end: string;
}

export type RecipientChannel = 'sms' | 'slack';

export interface Recipient {
  id: string;
  name: string;
  phone_e164: string | null;
  slack_channel_id: string | null;
  channel: RecipientChannel;
  /** Role slugs this person is nudged about, or every role. */
  roles: string[] | 'all';
  active: boolean;
  /** Stage 5: Innu user id — when it matches `lead.assigned_user_id`, only this person is nudged. */
  user_id?: string | null;
}

/** Stage 5 — "Have you spoken to…?" nudge timing (BUILD_PLAN Stage 5 contract). */
export interface NudgeSettings {
  enabled: boolean;
  /** Hours since the last team-member action before the question is texted (1–168). */
  after_hours: number;
  /** Hours before the same lead may be asked again (1–720). */
  cooldown_hours: number;
}

export const DEFAULT_NUDGE: NudgeSettings = { enabled: true, after_hours: 24, cooldown_hours: 48 };
export const NUDGE_LIMITS = { after_hours: { min: 1, max: 168 }, cooldown_hours: { min: 1, max: 720 } } as const;

/** The exact text a recipient receives, so Settings can quote it (worker `lib/nudge.ts`). */
export const NUDGE_QUESTION = 'Have you spoken to candidate {name} for {role} yet?';
export const NUDGE_REPLIES: ReadonlyArray<{ digit: '1' | '2' | '3' | '4'; label: string }> = [
  { digit: '1', label: 'Qualified – need to schedule interview' },
  { digit: '2', label: 'Disqualify' },
  { digit: '3', label: 'Hired' },
  { digit: '4', label: 'Not contacted' },
];
export const NUDGE_NOTES_QUESTION = "Got it. Any notes to add to {name}'s profile? Reply with your note, or NO.";

export interface SmsStatus {
  configured: boolean;
  dry_run: boolean;
  from_number?: string | null;
}

/** Stage 6 — Slack delivery status from the worker env (`INNU_CORE_INTERNAL_SECRET` present, dry run). */
export interface SlackStatus {
  configured: boolean;
  dry_run: boolean;
}

/** `GET /api/settings/connections` — what the company has connected in Innu → Connections. */
export interface ConnectionsStatus {
  slack: boolean;
  email: boolean;
  phone: boolean;
  /** Where to connect: `${INNU_SHELL_URL}/connections` (opened with `target="_top"` — we live in an iframe). */
  connections_url: string;
}

/** One row of `GET /api/settings/slack-channels` (`[]` when Slack is not connected). */
export interface SlackChannel {
  id: string;
  name: string;
}

export interface Settings {
  contact_hours: ContactHours;
  recipients: Recipient[];
  assessment: { auto_send: boolean };
  nudge: NudgeSettings;
  sms: SmsStatus;
  slack: SlackStatus;
}

export type SettingsPatch = Pick<Settings, 'contact_hours' | 'recipients' | 'assessment' | 'nudge'>;

export const DEFAULT_CONTACT_HOURS: ContactHours = { tz: 'America/Chicago', start: '08:00', end: '20:00' };

/** Short list; the worker accepts any IANA zone, so an unknown saved value is appended at render time. */
export const TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

/** Fills gaps so a partial or older worker payload still renders. */
export function normalizeSettings(raw: Partial<Settings> | null | undefined): Settings {
  return {
    contact_hours: { ...DEFAULT_CONTACT_HOURS, ...(raw?.contact_hours ?? {}) },
    recipients: (raw?.recipients ?? []).map((r) => ({
      id: r.id,
      name: r.name ?? '',
      phone_e164: r.phone_e164 ?? null,
      slack_channel_id: r.slack_channel_id ?? null,
      channel: r.channel ?? 'sms',
      roles: r.roles === 'all' || !Array.isArray(r.roles) ? 'all' : r.roles,
      active: r.active !== false,
      user_id: r.user_id?.trim() ? r.user_id.trim() : null,
    })),
    assessment: { auto_send: raw?.assessment?.auto_send !== false },
    nudge: {
      enabled: raw?.nudge?.enabled !== false,
      after_hours: finiteOr(raw?.nudge?.after_hours, DEFAULT_NUDGE.after_hours),
      cooldown_hours: finiteOr(raw?.nudge?.cooldown_hours, DEFAULT_NUDGE.cooldown_hours),
    },
    sms: { configured: raw?.sms?.configured === true, dry_run: raw?.sms?.dry_run !== false, from_number: raw?.sms?.from_number ?? null },
    slack: { configured: raw?.slack?.configured === true, dry_run: raw?.slack?.dry_run !== false },
  };
}

export function normalizeConnections(raw: Partial<ConnectionsStatus> | null | undefined): ConnectionsStatus {
  return {
    slack: raw?.slack === true,
    email: raw?.email === true,
    phone: raw?.phone === true,
    connections_url: typeof raw?.connections_url === 'string' && raw.connections_url ? raw.connections_url : 'https://innu.app/connections',
  };
}

export function normalizeChannels(raw: unknown): SlackChannel[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown }).items) ? (raw as { items: unknown[] }).items : [];
  return list.flatMap((c) => {
    if (!c || typeof c !== 'object') return [];
    const { id, name } = c as { id?: unknown; name?: unknown };
    if (typeof id !== 'string' || !id) return [];
    return [{ id, name: typeof name === 'string' && name ? name.replace(/^#/, '') : id }];
  });
}

function finiteOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export interface RecipientErrors {
  name?: string;
  phone?: string;
  slack_channel?: string;
}

/** Which recipient field an edit touched, so the page can clear just that inline error. */
export type RecipientField = keyof RecipientErrors | 'channel' | 'roles' | 'active' | 'user_id';

export interface NudgeErrors {
  after_hours?: string;
  cooldown_hours?: string;
}

export interface SettingsErrors {
  contact_hours?: { start?: string; end?: string; tz?: string };
  recipients: Record<string, RecipientErrors>;
  nudge?: NudgeErrors;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function hasErrors(e: SettingsErrors): boolean {
  return !!e.contact_hours || !!e.nudge || Object.keys(e.recipients).length > 0;
}

function hoursError(v: number, { min, max }: { min: number; max: number }): string | undefined {
  if (!Number.isInteger(v) || v < min || v > max) return `Enter a whole number of hours from ${min} to ${max}.`;
  return undefined;
}

/** Zod-like client validation; keyed so each message sits under its field. */
export function validateSettings(s: SettingsPatch): SettingsErrors {
  const errors: SettingsErrors = { recipients: {} };
  const ch: NonNullable<SettingsErrors['contact_hours']> = {};
  if (!s.contact_hours.tz.trim()) ch.tz = 'Pick a time zone.';
  if (!HHMM.test(s.contact_hours.start)) ch.start = 'Enter a start time.';
  if (!HHMM.test(s.contact_hours.end)) ch.end = 'Enter an end time.';
  if (!ch.start && !ch.end && s.contact_hours.end <= s.contact_hours.start) ch.end = 'End must be after start.';
  if (Object.keys(ch).length) errors.contact_hours = ch;

  for (const r of s.recipients) {
    const e: RecipientErrors = {};
    if (!r.name.trim()) e.name = 'Enter a name.';
    if (r.channel === 'sms' && !r.phone_e164) e.phone = 'Enter a mobile number, e.g. (713) 555-0101.';
    if (r.channel === 'slack' && !r.slack_channel_id) e.slack_channel = 'Pick the Slack channel this person reads.';
    if (Object.keys(e).length) errors.recipients[r.id] = e;
  }

  const n: NudgeErrors = {};
  const after = hoursError(s.nudge.after_hours, NUDGE_LIMITS.after_hours);
  const cooldown = hoursError(s.nudge.cooldown_hours, NUDGE_LIMITS.cooldown_hours);
  if (after) n.after_hours = after;
  if (cooldown) n.cooldown_hours = cooldown;
  if (Object.keys(n).length) errors.nudge = n;
  return errors;
}

export function newRecipient(): Recipient {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `tmp-${Date.now()}`;
  return { id, name: '', phone_e164: null, slack_channel_id: null, channel: 'sms', roles: 'all', active: true, user_id: null };
}
